import POIs from "./POIs";
import Schools from "./Schools";
import * as S from "./styles";
import { Scores as ScoresI } from "@local-logic/client";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import { Spacer, SVG, theme } from "@local-logic/core/ui";
import { Score as ScoreE, scorePois } from "@local-logic/elements-react";
import * as Accordion from "@radix-ui/react-accordion";
import { UseQueryResult } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import ChevronRightSVG from "~/assets/icons/chevron_right.svg";
import HomeSVG from "~/assets/icons/home.svg";
import PlaceSVG from "~/assets/icons/place.svg";
import { useStateManagerContext } from "~/context/stateManager";
import { useTracking } from "~/context/trackingProvider";
import useIsMobile from "~/hooks/useIsMobile";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { LocalContentProps } from "~/sdks/LocalContent/types";

interface ScoreProps {
  score: Partial<keyof ScoresI["location"]>;
  scoresData: UseQueryResult<ScoresI, unknown>["data"];
}

const Score = React.memo(({ score, scoresData }: ScoreProps) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const { activeScore } = useLocalContent();
  const tracking = useTracking();
  const isMobile = useIsMobile();

  const isActive = score === activeScore;

  const [isCtaClicked, setIsCtaClicked] = useState(false);

  const ctaClicked = () => {
    setIsCtaClicked(true);
  };

  useEffect(() => {
    if (isCtaClicked) {
      tracking.postEvent({
        eventType: TrackingEvents.LOCALCONTENT_LONG_DESCRIPTION_CLICKED,
        product: ProductNames.LOCAL_CONTENT,
        isMobile,
        eventAttributes: [
          {
            label: "activeScore",
            data: activeScore.toString(),
          },
        ],
      });
    }

    return () => {
      setIsCtaClicked(false);
    };
  }, [isCtaClicked]);

  if (!scoresData?.location[score]) {
    return null;
  }

  return (
    <Accordion.Item value={score} key={score}>
      <ScoreE.Root
        options={{
          score,
          scoresData,
        }}
      >
        <S.Header>
          <S.Trigger>
            <S.ChevronContainer>
              <SVG
                SVGElement={ChevronRightSVG as unknown as React.FC}
                fill={theme["--ll-color-on-background-variant1"]}
              />
            </S.ChevronContainer>
            <Spacer spacing="--ll-spacing-small" />
            <ScoreE.Icon />
            <Spacer spacing="--ll-spacing-small" />
            <ScoreE.Title />
            <Spacer />
            <ScoreE.Badge options={{ isPrimary: isActive }} />
          </S.Trigger>
        </S.Header>
        <S.Content>
          <S.InnerContent>
            <ScoreE.Description ctaClicked={ctaClicked} />
            <Spacer vertical spacing="--ll-spacing-small" />
            <ScoreE.BarChart
              options={{
                renderNeighbourhoodAvg: false,
                locationIcon: (sdkState.options?.marker?.icon === "pin"
                  ? PlaceSVG
                  : HomeSVG) as unknown as React.FC,
              }}
            />
            <S.POIContainer>
              {score === "high_schools" || score === "primary_schools" ? (
                <Schools score={score} />
              ) : (
                <POIs include={scorePois![score]!} />
              )}
            </S.POIContainer>
          </S.InnerContent>
        </S.Content>
      </ScoreE.Root>
    </Accordion.Item>
  );
});

Score.displayName = "Score";

export default Score;
